/**
 * Platform attribution / click ids for `/dye` — Facebook (`fbc`/`fbp`), Google
 * (`gclid`), Taboola/Outbrain (`tblci`).
 *
 * Reads cookies live on each call (not cached) so async FB pixels can set
 * `_fbp` / `_fbc` after init. Gated on the media-buying flag.
 */

import type { EventParams } from './track';
import { getCookie, setCookie } from '../../util/cookies';
import { warn } from '../../util/log';
import { isMediaBuyingEnabled } from '../state';

export interface AttributionIds {
  fbc?: string;
  fbp?: string;
  gclid?: string;
  tblci?: string;
}

export interface CollectAttributionIdsOptions {
  /** When true, persist synthesized or QS-derived ids into cookies (1 day). */
  persistCookies?: boolean;
}

function currentQueryParams(): Record<string, string> {
  try {
    if (typeof location === 'undefined') return {};
    const out: Record<string, string> = {};
    for (const [key, value] of new URL(location.href).searchParams.entries()) {
      out[key] = value;
    }
    return out;
  } catch {
    return {};
  }
}

/**
 * Collect Facebook / Google / platform click ids without mutating a target object.
 */
export function collectAttributionIds(opts: CollectAttributionIdsOptions = {}): AttributionIds {
  if (!isMediaBuyingEnabled()) {
    return {};
  }

  const qs = currentQueryParams();
  const persist = opts.persistCookies === true;
  const out: AttributionIds = {};

  try {
    let fbc = getCookie('_fbc') ?? undefined;
    if (!fbc && qs['fbclid']) {
      fbc = ['fb.1', String(Date.now()), qs['fbclid']].join('.');
      if (persist) {
        try {
          setCookie('_fbc', fbc, { days: 1 });
        } catch (e) {
          warn('could not save fbc to cookies', e);
        }
      }
    }
    if (fbc) out.fbc = fbc;
  } catch (e) {
    warn('could not extract fbc', e);
  }

  try {
    const fbp = getCookie('_fbp') ?? undefined;
    if (fbp) out.fbp = fbp;
  } catch (e) {
    warn('could not extract fbp', e);
  }

  try {
    let gclid = getCookie('_gclid') ?? undefined;
    if (!gclid && qs['gclid']) {
      gclid = qs['gclid'];
      if (persist) {
        try {
          setCookie('_gclid', gclid, { days: 1 });
        } catch (e) {
          warn('could not save gclid to cookies', e);
        }
      }
    }
    if (gclid) out.gclid = gclid;
  } catch (e) {
    warn('could not extract gclid', e);
  }

  try {
    let tblci = getCookie('_tblci') ?? undefined;
    const qsClickId = qs['tblci'] || qs['mbclid'] || qs['dicbo'];
    if (!tblci && qsClickId) {
      tblci = qsClickId;
      if (persist) {
        try {
          setCookie('_tblci', tblci, { days: 1 });
        } catch (e) {
          warn('could not save tblci to cookies', e);
        }
      }
    }
    if (tblci) out.tblci = tblci;
  } catch (e) {
    warn('could not extract tblci', e);
  }

  return out;
}

/** Merge attribution ids into `target` without overwriting keys the caller already set. */
export function mergeAttributionIdsInto(
  target: EventParams,
  opts?: CollectAttributionIdsOptions,
): void {
  if (!isMediaBuyingEnabled()) return;
  const ids = collectAttributionIds(opts);
  if (ids.fbc != null && target.fbc === undefined) target.fbc = ids.fbc;
  if (ids.fbp != null && target.fbp === undefined) target.fbp = ids.fbp;
  if (ids.gclid != null && target.gclid === undefined) target.gclid = ids.gclid;
  if (ids.tblci != null && target.tblci === undefined) target.tblci = ids.tblci;
}
